import { createSlice, isAnyOf } from "@reduxjs/toolkit"
import { createAppAsyncThunk } from "@app/createAppAsyncThunk"
import { showErrorSnackbar } from "@features/ui/uiSlice"
import { RootState } from "@app/store"
import { ConsultantClient } from "@clients/consultantClient"
import { LeadRoutingClient } from "@clients/leadRoutingClient"
import {
  ConsultantSummary,
  Coverage,
  CreateRuleRequest,
  Dashboard,
  Preview,
  RoutingSettings,
  Rule,
  UpdateRuleRequest,
} from "./leadRouting.types"

export type Loadable<T> =
  | { status: "idle" }
  | { status: "pending" }
  | { status: "success"; data: T }
  | { status: "error"; error?: string }

interface LeadRoutingState {
  rules: Loadable<Rule[]>
  settings: Loadable<RoutingSettings>
  consultants: Loadable<ConsultantSummary[]>
  dashboard: Loadable<Dashboard>
  preview: Loadable<Preview>
  coverage: Loadable<Coverage>
}

const initialState: LeadRoutingState = {
  rules: { status: "idle" },
  settings: { status: "idle" },
  consultants: { status: "idle" },
  dashboard: { status: "idle" },
  preview: { status: "idle" },
  coverage: { status: "idle" },
}

async function withSnackbar<T>(
  dispatch: (action: unknown) => void,
  fn: () => Promise<T>,
): Promise<T> {
  try {
    return await fn()
  } catch (e) {
    dispatch(showErrorSnackbar((e as Error).message))
    throw e
  }
}

export const fetchRules = createAppAsyncThunk("leadRouting/fetchRules", () =>
  LeadRoutingClient.listRules(),
)

export const fetchSettings = createAppAsyncThunk(
  "leadRouting/fetchSettings",
  () => LeadRoutingClient.getSettings(),
)

export const fetchConsultants = createAppAsyncThunk(
  "leadRouting/fetchConsultants",
  async () => {
    const consultants = await ConsultantClient.listAllConsultants()
    const byId = new Map<string, ConsultantSummary>()
    consultants.forEach((c) =>
      byId.set(c.id, {
        id: c.id,
        name: c.name,
        email: c.email,
        acceptedQtfs: c.acceptedQtfs,
        languages: c.languages,
        tags: (c as unknown as { tags?: string[] }).tags ?? [],
        programType: c.programType ?? undefined,
        isPlacementExpert: c.isPlacementExpert,
      }),
    )
    return Array.from(byId.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    )
  },
)

export const fetchDashboard = createAppAsyncThunk(
  "leadRouting/fetchDashboard",
  (windowDays: number) => LeadRoutingClient.getDashboard(windowDays),
)

export const fetchPreview = createAppAsyncThunk(
  "leadRouting/fetchPreview",
  () => LeadRoutingClient.getPreview(),
)

export const fetchCoverage = createAppAsyncThunk(
  "leadRouting/fetchCoverage",
  () => LeadRoutingClient.getRuleCoverage(),
)

export const createRule = createAppAsyncThunk(
  "leadRouting/createRule",
  (body: CreateRuleRequest, { dispatch }) =>
    withSnackbar(dispatch, async () => {
      await LeadRoutingClient.createRule(body)
      return LeadRoutingClient.listRules()
    }),
)

export const updateRule = createAppAsyncThunk(
  "leadRouting/updateRule",
  (arg: { id: string; body: UpdateRuleRequest }, { dispatch }) =>
    withSnackbar(dispatch, async () => {
      await LeadRoutingClient.updateRule(arg.id, arg.body)
      return LeadRoutingClient.listRules()
    }),
)

export const deleteRule = createAppAsyncThunk(
  "leadRouting/deleteRule",
  (id: string, { dispatch }) =>
    withSnackbar(dispatch, async () => {
      await LeadRoutingClient.deleteRule(id)
      return LeadRoutingClient.listRules()
    }),
)

export const reorderRules = createAppAsyncThunk(
  "leadRouting/reorderRules",
  (orderedRuleIds: string[], { dispatch }) =>
    withSnackbar(dispatch, () =>
      LeadRoutingClient.reorderRules(orderedRuleIds),
    ),
)

export const publishRule = createAppAsyncThunk(
  "leadRouting/publishRule",
  (id: string, { dispatch }) =>
    withSnackbar(dispatch, async () => {
      await LeadRoutingClient.publishRule(id)
      return LeadRoutingClient.listRules()
    }),
)

export const setRuleDisabled = createAppAsyncThunk(
  "leadRouting/setRuleDisabled",
  (arg: { id: string; disabled: boolean }, { dispatch }) =>
    withSnackbar(dispatch, async () => {
      await LeadRoutingClient.setRuleDisabled(arg.id, arg.disabled)
      return LeadRoutingClient.listRules()
    }),
)

export const updateSettings = createAppAsyncThunk(
  "leadRouting/updateSettings",
  (patch: Partial<RoutingSettings>, { dispatch }) =>
    withSnackbar(dispatch, () => LeadRoutingClient.updateSettings(patch)),
)

const leadRoutingSlice = createSlice({
  name: "leadRouting",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRules.pending, (state) => {
        state.rules = { status: "pending" }
      })
      .addCase(fetchRules.rejected, (state, action) => {
        state.rules = { status: "error", error: action.error.message }
      })
      .addCase(fetchSettings.pending, (state) => {
        if (state.settings.status === "idle") {
          state.settings = { status: "pending" }
        }
      })
      .addCase(fetchConsultants.pending, (state) => {
        state.consultants = { status: "pending" }
      })
      .addCase(fetchConsultants.rejected, (state, action) => {
        state.consultants = { status: "error", error: action.error.message }
      })
      .addCase(fetchConsultants.fulfilled, (state, action) => {
        state.consultants = { status: "success", data: action.payload }
      })
      .addCase(fetchDashboard.pending, (state) => {
        state.dashboard = { status: "pending" }
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.dashboard = { status: "error", error: action.error.message }
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.dashboard = { status: "success", data: action.payload }
      })
      .addCase(fetchPreview.pending, (state) => {
        state.preview = { status: "pending" }
      })
      .addCase(fetchPreview.rejected, (state, action) => {
        state.preview = { status: "error", error: action.error.message }
      })
      .addCase(fetchPreview.fulfilled, (state, action) => {
        state.preview = { status: "success", data: action.payload }
      })
      .addCase(fetchCoverage.pending, (state) => {
        state.coverage = { status: "pending" }
      })
      .addCase(fetchCoverage.rejected, (state, action) => {
        state.coverage = { status: "error", error: action.error.message }
      })
      .addCase(fetchCoverage.fulfilled, (state, action) => {
        state.coverage = { status: "success", data: action.payload }
      })
      .addMatcher(
        isAnyOf(
          fetchRules.fulfilled,
          createRule.fulfilled,
          updateRule.fulfilled,
          deleteRule.fulfilled,
          reorderRules.fulfilled,
          publishRule.fulfilled,
          setRuleDisabled.fulfilled,
        ),
        (state, action) => {
          state.rules = { status: "success", data: action.payload }
        },
      )
      .addMatcher(
        isAnyOf(fetchSettings.fulfilled, updateSettings.fulfilled),
        (state, action) => {
          state.settings = { status: "success", data: action.payload }
        },
      )
  },
})

export const selectRules = (state: RootState): Loadable<Rule[]> =>
  state.leadRouting.rules
export const selectRoutingSettings = (
  state: RootState,
): Loadable<RoutingSettings> => state.leadRouting.settings
export const selectRoutingConsultants = (
  state: RootState,
): Loadable<ConsultantSummary[]> => state.leadRouting.consultants
export const selectDashboard = (state: RootState): Loadable<Dashboard> =>
  state.leadRouting.dashboard
export const selectPreview = (state: RootState): Loadable<Preview> =>
  state.leadRouting.preview
export const selectCoverage = (state: RootState): Loadable<Coverage> =>
  state.leadRouting.coverage

export default leadRoutingSlice.reducer
